import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { Box, CircularProgress, IconButton, InputBase, Paper, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import MicIcon from '@mui/icons-material/Mic';

import { glassSurface, glassText } from '../utils/glassSurface';
import { useCommandContext } from '../context/CommandContext';
import { API_BASE_URL } from '../constants';
import { useSpeechRecognition } from '../hooks/useSpeechRecognition';
import {
  PHASE, TICK_MS, describeAction, describeUsage, formatSeconds, formatUsage, phaseLabel,
} from '../utils/copilotPhases';

/**
 * THE COPILOT BAR — a sentence in, chart commands out.
 *
 * <h2>⚠ MODELESS, AND THAT IS THE WHOLE POINT OF THE COMPONENT</h2>
 *
 * It is a plain `Paper` in a portal, not a MUI `Dialog`. A dialog brings a backdrop and a focus
 * trap, and both are fatal here: the backdrop swallows every click on the chart the answer is
 * being applied to, so the user could not watch the thing they just asked for happen. The same
 * reasoning the Interactive Trade Simulator panel records for being modeless.
 *
 * <h2>⚠ A PORTAL TO `document.body`, AND Z-INDEX ALONE WOULD NOT HAVE DONE IT</h2>
 *
 * The bar used to be an absolutely-positioned child of the chart wrapper at `zIndex: 20`, which
 * put it UNDER every MUI dialog (backdrop 1300) — so `Ctrl+K` with a study dialog open appeared
 * to do nothing. Raising the number inside that subtree is not a fix: an ancestor with a
 * `transform` (or a `filter`, or `will-change`) creates a containing block its children cannot
 * paint out of AT ANY z-index, and the chart's own wrapper is exactly the kind of element that
 * acquires one. Leaving the subtree is what makes the stacking order ours to set.
 *
 * <p>`position: fixed`, not `absolute`: in a portal to `body` there is no positioned ancestor, so
 * `absolute` resolves against the initial containing block and the bar would scroll away with the
 * document.
 *
 * <h2>Cmd+K / Ctrl+K, in the CAPTURE phase</h2>
 *
 * Toggles. Opening focuses and SELECTS, so a second query overwrites the first without the user
 * clearing it. Escape closes — and while the input has focus Escape is handled on the input
 * rather than bubbling, or it would also dismiss whatever the chart has open behind.
 *
 * <p>⚠ THE HOTKEY LISTENER IS `capture: true` ON `window`. An open dialog's own keydown handling
 * runs in the BUBBLE phase on the dialog's node, so a bubble listener on `window` sees the event
 * last and a `stopPropagation()` anywhere in between swallows it. Capture on `window` is the
 * first thing in the path, which no descendant can pre-empt.
 *
 * <p>⚠ ESCAPE IS DELIBERATELY NOT IN THAT LISTENER. Moving it there would make the Copilot eat
 * Escape from every dialog in the application — the exact inverse of the problem being fixed.
 *
 * <p>⚠ IT MUST NOT FIRE WHILE THE USER IS TYPING ELSEWHERE. The symbol box and every dialog
 * field are one tab away, and a global key handler that ignores that is the defect the replay
 * hotkeys already record. Both shortcuts here are MODIFIED so they cannot collide with ordinary
 * typing; Escape is not, which is why Escape is only honoured when this bar owns the focus.
 */

/** Rendered under the input. A refusal is not an error — it is an answer. */
const TONE = { INFO: 'info', REFUSED: 'refused', FAILED: 'failed' };

/**
 * Above MUI's whole ladder: Dialog backdrop 1300, Tooltip 1400.
 *
 * <p>Named rather than inline because the number only means something RELATIVE to those two, and
 * a bare 1500 in an `sx` block reads as a round number somebody liked.
 */
export const COPILOT_Z_INDEX = 1500;

export function ChartCopilotBar({ api, activeSymbol, enabled }) {
  const { executeAppCommands } = useCommandContext();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState(null);
  const inputRef = useRef(null);
  const rootRef = useRef(null);

  /* ── Operational state: what is happening, and how long it has been happening ───────────── */
  const [phase, setPhase] = useState(PHASE.IDLE);
  const [action, setAction] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [totalMs, setTotalMs] = useState(null);
  const [usage, setUsage] = useState(null);
  const [model, setModel] = useState(null);
  const [providerMs, setProviderMs] = useState(null);
  const startedAtRef = useRef(0);

  /* ── Voice ──────────────────────────────────────────────────────────────────────────────── */
  const handleTranscript = useCallback((next) => setText(next), []);
  const handleProblem = useCallback(
    (message) => setNote({ tone: TONE.FAILED, text: message }), [],
  );
  const speech = useSpeechRecognition({
    onTranscript: handleTranscript, onProblem: handleProblem,
  });

  // ⚠ READ THROUGH REFS BY THE KEY LISTENER. That listener is bound ONCE — rebinding it on every
  // keystroke would add and remove a window listener per character — so it cannot close over
  // `text` or over the hook's return value, both of which are new on every render.
  const speechRef = useRef(speech);
  const textRef = useRef(text);
  useEffect(() => { speechRef.current = speech; });
  useEffect(() => { textRef.current = text; });

  /* ── Cmd+K / Ctrl+K, and Alt+V ──────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      const key = String(e.key || '').toLowerCase();
      if ((e.metaKey || e.ctrlKey) && !e.altKey && key === 'k') {
        e.preventDefault();          // Ctrl+K is "focus the search bar" in Chrome too
        setOpen(prev => !prev);
        return;
      }
      // ⚠ `e.code` AS WELL AS `e.key`. On macOS Alt+V produces the character `√`, so a
      // key-only test binds a shortcut that silently never fires on half the platforms —
      // and `preventDefault` is what stops that character being typed into the field.
      const isV = key === 'v' || e.code === 'KeyV';
      if (e.altKey && !e.ctrlKey && !e.metaKey && isV) {
        // Unsupported browsers get NO binding rather than a key that returns early: a dead
        // shortcut is the thing somebody reports as broken, and there is nothing to report.
        if (!speechRef.current?.supported) return;
        e.preventDefault();
        // Opens the bar first. A global voice hotkey that needed the bar open already would
        // do nothing on the press a reader would actually make.
        setOpen(true);
        speechRef.current.toggle(textRef.current);
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled]);

  /**
   * ⚠ MUI'S FOCUS TRAP WOULD TAKE THE FOCUS STRAIGHT BACK, and this is the escape.
   *
   * Read off MUI 7.1.2's own `FocusTrap`: it binds `focusin` on `document` in the BUBBLE phase,
   * and when `activeElement` is outside the dialog's root — which our portal always is — it
   * calls `rootElement.focus()`. There is no opt-out attribute. So the bar would open above the
   * modal, exactly as asked, and lose the caret on the same frame.
   *
   * <p>A capture-phase listener on `document` runs BEFORE that bubble listener, so
   * `stopImmediatePropagation` keeps the trap from ever seeing our own focus events. It is
   * scoped to this subtree: the trap still contains focus for everything else, which the
   * app-wide alternative (`MuiModal.defaultProps.disableEnforceFocus`) would not — that also
   * disables the Tab loop inside every dialog, which is a real accessibility regression in
   * exchange for a one-line diff.
   *
   * <p>⚠ KNOWN COST: React attaches its own listeners above us, so our input's React `focus`
   * event is suppressed with the trap's. Only MUI's `focused` ring depends on it — the caret,
   * typing and `onKeyDown` are untouched — so the bar carries its own `:focus-within` border
   * instead of relying on that class.
   */
  useEffect(() => {
    if (!open) return undefined;
    const keepFocus = (e) => {
      if (rootRef.current && rootRef.current.contains(e.target)) e.stopImmediatePropagation();
    };
    document.addEventListener('focusin', keepFocus, true);
    return () => document.removeEventListener('focusin', keepFocus, true);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    // A frame late: the input does not exist until this render has committed.
    const id = window.setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open]);

  /** The elapsed readout. Stops itself the moment the phase stops waiting. */
  useEffect(() => {
    if (phase !== PHASE.INTERPRETING && phase !== PHASE.EXECUTING) return undefined;
    const id = window.setInterval(
      () => setElapsedMs(Date.now() - startedAtRef.current), TICK_MS,
    );
    return () => window.clearInterval(id);
  }, [phase]);

  const submit = useCallback(async () => {
    const query = text.trim();
    if (!query || busy) return;
    if (speech.listening) speech.stop();   // Enter means "that is the query", so stop dictating
    setBusy(true);
    setNote(null);
    setAction(null);
    setTotalMs(null);
    // Cleared at the START of a query, not at the end of the last one: a counter left standing
    // would be read as this query's cost while this query was still running.
    setUsage(null);
    setProviderMs(null);
    setElapsedMs(0);
    startedAtRef.current = Date.now();
    setPhase(PHASE.INTERPRETING);
    try {
      const { data } = await api.post(`${API_BASE_URL}/api/copilot/query`, {
        query, activeSymbol: activeSymbol || '',
      });

      const commands = Array.isArray(data?.commands) ? data.commands
        : (data?.command ? [data.command] : []);
      const explanation = data?.explanation || '';
      setUsage(data?.usage ?? null);
      setModel(data?.model ?? null);
      setProviderMs(Number.isFinite(data?.providerMs) ? data.providerMs : null);

      if (commands.length === 0) {
        // The platform-boundary answer lands here, and it is a COMPLETE answer rather than a
        // failure: the backend pruned the tool so Claude explained instead of calling it.
        // It never reaches EXECUTING, because there is nothing to execute.
        setNote({ tone: TONE.INFO, text: explanation || 'Nothing to do for that.' });
        return;
      }

      setAction(describeAction(commands));
      setPhase(PHASE.EXECUTING);
      const outcome = await executeAppCommands(commands);
      const refused = outcome.results.find(r => !r.ok);
      if (refused) {
        setNote({
          tone: TONE.REFUSED,
          text: `${explanation ? `${explanation} ` : ''}— ${refused.refusal?.message
            || 'That command was refused.'}`,
        });
      } else {
        setNote({ tone: TONE.INFO, text: explanation || 'Done.' });
      }
    } catch (e) {
      // ⚠ NEVER THROWS INTO THE TREE. There is no error boundary around the chart, so an
      // unhandled rejection here would blank the whole view over a failed fetch.
      const status = e?.response?.status;
      setNote({
        tone: TONE.FAILED,
        text: status === 403
          ? 'The Copilot is not enabled for this account.'
          : (e?.response?.data?.explanation || 'The Copilot could not be reached.'),
      });
    } finally {
      setTotalMs(Date.now() - startedAtRef.current);
      setPhase(PHASE.DONE);
      setBusy(false);
    }
  }, [api, activeSymbol, text, busy, executeAppCommands, speech]);

  if (!enabled || !open) return null;

  const toneColour = {
    [TONE.INFO]: glassText(0.92),
    [TONE.REFUSED]: '#f0b429',   // amber: refused is a decision, not a fault
    [TONE.FAILED]: '#f4777f',
  };

  // Listening is owned by the speech hook and everything else by `submit`, so the displayed
  // phase is DERIVED rather than stored a second time — a `setPhase(LISTENING)` beside the
  // hook's own state is two answers to "is the microphone on", and the one that gets stuck is
  // the one with the pulsing indicator attached to it.
  const shownPhase = speech.listening ? PHASE.LISTENING : phase;
  const statusText = phaseLabel(shownPhase, { elapsedMs, action, totalMs });
  // ⚠ THE COUNTER'S TIME IS THE MODEL'S, AND "Completed in" IS THE WHOLE ROUND TRIP. They are
  // different numbers on purpose: side by side they say how much of the wait was the provider
  // and how much was this application. The tooltip names both.
  const usageText = formatUsage(usage, providerMs);
  const usageTitle = [
    describeUsage(usage, model),
    providerMs == null ? null : `model ${formatSeconds(providerMs)} of ${formatSeconds(totalMs)} total`,
  ].filter(Boolean).join(' · ');

  return createPortal(
    <Box
      ref={rootRef}
      data-testid="copilot-bar"
      sx={{
        position: 'fixed', top: 16, left: '50%', transform: 'translateX(-50%)',
        zIndex: COPILOT_Z_INDEX, width: 'min(680px, calc(100% - 32px))',
        // The chart owns the clicks everywhere this component is not — and at this z-index that
        // matters far more than it did at 20: a full-width pointer-catching layer above every
        // dialog would make the whole application unclickable.
        pointerEvents: 'none',
      }}
    >
      <Paper
        elevation={0}
        sx={{ ...glassSurface(), display: 'flex', flexDirection: 'column',
          px: 1.5, py: 0.75, pointerEvents: 'auto',
          // Our own ring, because the React focus event that drives MUI's is suppressed — see
          // the focus-trap note above.
          '&:focus-within': { outline: `1px solid ${glassText(0.35)}`, outlineOffset: -1 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: glassText(0.75) }} />
          <InputBase
            inputRef={inputRef}
            value={text}
            placeholder={activeSymbol
              ? `Ask for a chart setup — e.g. "show AAPL with RSI"  (${activeSymbol})`
              : 'Ask for a chart setup…'}
            onChange={e => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submit(); }
              // Handled here, not globally: Escape must not also close what is behind the chart.
              if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
            }}
            inputProps={{ 'aria-label': 'Chart Copilot', 'data-testid': 'copilot-input' }}
            sx={{ flex: 1, color: glassText(1), fontSize: 14 }}
          />
          {/* Hidden, not disabled, where the browser has no Web Speech API: a control that is
              present and inert has nothing on screen to explain it and nothing in the console
              either. Chrome exposes it on a SECURE ORIGIN only, so plain http has none. */}
          {speech.supported && (
            <IconButton
              size="small"
              data-testid="copilot-mic"
              aria-label={speech.listening ? 'Stop dictating' : 'Dictate a query'}
              aria-pressed={speech.listening}
              onClick={() => speech.toggle(text)}
              sx={{
                color: speech.listening ? '#f4777f' : glassText(0.6),
                '@keyframes copilotPulse': {
                  '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.25 },
                },
                animation: speech.listening ? 'copilotPulse 1.1s ease-in-out infinite' : 'none',
              }}
            >
              <MicIcon fontSize="inherit" />
            </IconButton>
          )}
          {busy && <CircularProgress size={16} sx={{ color: glassText(0.75) }} />}
          <IconButton size="small" aria-label="Close the Copilot"
            onClick={() => setOpen(false)} sx={{ color: glassText(0.6) }}>
            <CloseIcon fontSize="inherit" />
          </IconButton>
        </Box>

        {(statusText || usageText) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25, minHeight: 15 }}>
            <Typography data-testid="copilot-phase"
              sx={{ fontSize: 11, color: glassText(0.6), fontVariantNumeric: 'tabular-nums' }}>
              {statusText}
            </Typography>
            <Box sx={{ flex: 1 }} />
            {usageText && (
              <Typography data-testid="copilot-usage" title={usageTitle}
                sx={{ fontSize: 11, color: glassText(0.5), fontVariantNumeric: 'tabular-nums' }}>
                {usageText}
              </Typography>
            )}
          </Box>
        )}
      </Paper>

      {note && (
        <Paper
          elevation={0}
          data-testid="copilot-note"
          sx={{ ...glassSurface(), mt: 1, px: 1.5, py: 1, pointerEvents: 'auto' }}
        >
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.5, color: toneColour[note.tone] }}>
            {note.text}
          </Typography>
        </Paper>
      )}
    </Box>,
    document.body,
  );
}

ChartCopilotBar.propTypes = {
  /** The authenticated axios client. */
  api: PropTypes.object.isRequired,
  /** The ticker currently charted, sent so the model can default to it. */
  activeSymbol: PropTypes.string,
  /** False hides the bar AND unbinds both hotkeys — see the note in App. */
  enabled: PropTypes.bool,
};

export default ChartCopilotBar;
